import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, BookOpen, Clock, Pill, AlertTriangle, ExternalLink, ShoppingBag } from "lucide-react";
import { detectRegion, regionLabel, openAffiliate } from "@/lib/affiliate";

export default function SupplementDetail() {
  const { key } = useParams();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [region] = useState(detectRegion());

  useEffect(() => {
    api
      .get(`/supplements/${key}`, { params: { region } })
      .then((r) => setS(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Not found"))
      .finally(() => setLoading(false));
  }, [key, region]);

  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16 text-center">
        <p style={{ color: "var(--text-2)" }}>Loading…</p>
      </main>
    );
  }
  if (err || !s) {
    return (
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
        <p className="font-serif text-2xl">{err || "Not found"}</p>
        <Link to="/" className="btn-ghost text-sm mt-6 inline-flex" data-testid="supp-back">Home</Link>
      </main>
    );
  }

  return (
    <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16" data-testid={`supplement-detail-${s.key}`}>
      <button
        onClick={() => window.history.back()}
        className="btn-ghost text-sm flex items-center gap-1.5 mb-10"
        data-testid="supp-back"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
      </button>

      <p className="label-eyebrow mb-2">{s.category}</p>
      <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-5">{s.name}</h1>
      <p className="text-lg leading-relaxed mb-10" style={{ color: "var(--text-2)" }}>
        {s.benefits}
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <div className="card-soft p-5">
          <Pill className="w-4 h-4 mb-2" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
          <p className="label-eyebrow mb-1">Dosage</p>
          <p>{s.dosage}</p>
        </div>
        <div className="card-soft p-5">
          <Clock className="w-4 h-4 mb-2" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
          <p className="label-eyebrow mb-1">Timing</p>
          <p>{s.timing}</p>
        </div>
      </div>

      <div className="p-5 rounded-2xl mb-10" style={{ background: "#FBF4F0", border: "1px solid #ECDCD3" }}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" strokeWidth={1.5} style={{ color: "var(--warning)" }} />
          <div className="space-y-3">
            <div>
              <p className="label-eyebrow mb-1" style={{ color: "var(--warning)" }}>Warnings</p>
              <p className="text-sm">{s.warnings}</p>
            </div>
            <div>
              <p className="label-eyebrow mb-1" style={{ color: "var(--warning)" }}>Interactions</p>
              <p className="text-sm">{s.interactions}</p>
            </div>
          </div>
        </div>
      </div>

      {s.affiliate_links && s.affiliate_links.length > 0 && (
        <section className="mb-10" data-testid="where-to-buy">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingBag className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
            <h2 className="font-serif text-2xl">Where to buy</h2>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--text-2)" }}>
            Showing retailers for {regionLabel(region)}. Some are affiliate links — they help support Alternavit at no extra cost to you.
          </p>
          <div className="grid sm:grid-cols-2 gap-2">
            {s.affiliate_links.map((link) => (
              <button
                key={link.retailer}
                onClick={() =>
                  openAffiliate({
                    supplement_key: s.key,
                    retailer: link.retailer,
                    region,
                    url: link.url,
                  })
                }
                className="card-soft p-4 text-left flex items-center justify-between hover:bg-[var(--bg-2)] transition-colors"
                data-testid={`buy-${s.key}-${link.retailer}`}
              >
                <span className="text-sm" style={{ color: "var(--text)" }}>{link.label}</span>
                <ExternalLink className="w-4 h-4 shrink-0" strokeWidth={1.5} style={{ color: "var(--text-2)" }} />
              </button>
            ))}
          </div>
        </section>
      )}

      <div className="mb-3 flex items-center gap-2">
        <BookOpen className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--brand)" }} />
        <h2 className="font-serif text-2xl">Research & citations</h2>
      </div>
      {s.citations && s.citations.length > 0 ? (
        <ul className="space-y-3">
          {s.citations.map((c, i) => (
            <li key={i} className="card-soft p-4" data-testid={`citation-${i}`}>
              <a
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start justify-between gap-3 hover:underline"
              >
                <div>
                  <p className="text-sm" style={{ color: "var(--text)" }}>{c.title}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--text-2)" }}>{c.source}</p>
                </div>
                <ExternalLink className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} style={{ color: "var(--text-2)" }} />
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm" style={{ color: "var(--text-2)" }}>Citations coming soon.</p>
      )}

      <p className="text-xs mt-12" style={{ color: "var(--text-2)" }}>
        Educational only. Not a substitute for personalised medical advice.
      </p>
    </main>
  );
}
